import { Helmet } from 'react-helmet-async';
import { SOCIAL_LINKS } from '../utils/socialLinks';

const SEO = ({
  // Varsayılanlar ana sayfayı tanımlar; hiçbir sayfa yanlışlıkla "Üyelik Başvurusu" başlığıyla indekslenmesin
  title = 'İskoçya Fenerbahçeliler Derneği | Fenerbahçe Scotland',
  description = 'İskoçya Fenerbahçeliler Derneği (Fenerbahçe Scotland), Edinburgh merkezli olarak İskoçya\'daki Fenerbahçelileri buluşturuyor. Üyelik, etkinlikler ve haberler.',
  keywords = 'İskoçya Fenerbahçeliler Derneği, Fenerbahçe Scotland, Fenerbahce Scotland, Fenerbahçe Edinburgh, Fenerbahçe taraftar derneği, İskoçya Türk derneği',
  image = '/og-image.jpg?v=2',
  url = '/',
  type = 'website',
  structuredData,
  noindex = false,
  breadcrumbName,
}) => {
  const fullTitle = title.includes('Fenerbahçe') || title.includes('İskoçya') ? title : `${title} | İskoçya Fenerbahçeliler Derneği`;
  
  // Absolute URL için origin belirleme (client-side ve server-side uyumlu)
  const getOrigin = () => {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.origin;
    }
    // Fallback: environment variable veya default
    if (typeof process !== 'undefined' && process.env.REACT_APP_SITE_URL) {
      return process.env.REACT_APP_SITE_URL;
    }
    // Production için default domain
    return 'https://fenerbahcescotland.org.uk';
  };
  
  const origin = getOrigin();
  // Logo için mutlaka absolute URL kullan (WhatsApp, Facebook vb. için gerekli)
  const fullImage = image.startsWith('http') ? image : `${origin}${image.startsWith('/') ? image : '/' + image}`;
  const fullUrl = url.startsWith('http') ? url : `${origin}${url.startsWith('/') ? url : '/' + url}`;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': ['SportsOrganization', 'Organization'],
    '@id': `${origin}/#organization`,
    name: 'İskoçya Fenerbahçeliler Derneği',
    // Arama davranışındaki yazım varyasyonları (ç/c, TR/EN) burada karşılanır
    alternateName: [
      'Fenerbahçe Scotland',
      'Fenerbahce Scotland',
      'Iskocya Fenerbahceliler Dernegi',
      'Fenerbahçe İskoçya',
    ],
    url: origin,
    logo: {
      '@type': 'ImageObject',
      url: `${origin}/logo-iskocya.png`,
      caption: 'İskoçya Fenerbahçeliler Derneği logosu',
    },
    image: `${origin}/og-image.jpg`,
    description: 'Edinburgh merkezli, İskoçya genelinde faaliyet gösteren ve Fenerbahçe Spor Kulübü\'ne gönül vermiş bireyleri bir araya getiren resmi taraftar derneği.',
    sport: 'Football',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '23 Jocks Lodge',
      addressLocality: 'Edinburgh',
      postalCode: 'EH8 7AA',
      addressRegion: 'Scotland',
      addressCountry: 'GB',
    },
    email: 'info@iskocyafenerbahcelilerdernegi.org.uk',
    telephone: '+44 7564 441881',
    areaServed: {
      '@type': 'AdministrativeArea',
      name: 'Scotland',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'info@iskocyafenerbahcelilerdernegi.org.uk',
      telephone: '+44 7564 441881',
      areaServed: 'GB',
      availableLanguage: ['Turkish', 'English'],
    },
    // sameAs yalnızca **derneğin kendi** profillerini listeler. Buradaki
    // 'fenerbahcescotland' adresleri var olmayan hesaplardı; arama motorlarına
    // yanlış profil bildiriyorlardı. Fenerbahçe SK'nın hesapları buraya konmaz —
    // onlar derneğe ait değil (sitedeki bağlantıları için bkz. utils/socialLinks).
    sameAs: [SOCIAL_LINKS.instagram],
  };

  // Breadcrumb sayfaya göre üretilir (anasayfada tek adım kalır)
  const isHome = fullUrl === origin || fullUrl === `${origin}/`;
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Anasayfa',
        item: origin,
      },
      ...(isHome ? [] : [{
        '@type': 'ListItem',
        position: 2,
        name: breadcrumbName || title.split(' - ')[0].split(' | ')[0],
        item: fullUrl,
      }]),
    ],
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: fullTitle,
    description,
    url: fullUrl,
    inLanguage: 'tr-TR',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${origin}/#website`,
      name: 'İskoçya Fenerbahçeliler Derneği',
      alternateName: 'Fenerbahçe Scotland',
      url: origin,
      inLanguage: 'tr-TR',
      publisher: { '@id': `${origin}/#organization` },
    },
    about: { '@id': `${origin}/#organization` },
    breadcrumb: breadcrumbSchema,
  };

  return (
    <Helmet>
      <html lang="tr" />
      
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="İskoçya Fenerbahçeliler Derneği" />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="language" content="Turkish" />
      <meta name="revisit-after" content="7 days" />
      <meta name="geo.region" content="GB-SCT" />
      <meta name="geo.placename" content="Scotland" />
      
      <link rel="canonical" href={fullUrl} />
      
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content="İskoçya Fenerbahçeliler Derneği" />
      <meta property="og:locale" content="tr_TR" />
      <meta property="og:locale:alternate" content="en_GB" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={title} />
      
      <meta name="theme-color" content="#1F3A65" />
      <meta name="msapplication-TileColor" content="#1F3A65" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="FB İskoçya" />
      
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
      
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
